import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const benefits = [
    {
      id: 1,
      icon: 'Clock',
      title: 'Fast Turnaround',
      description: 'Deliver MVP in 8-12 weeks with agile methodology and rapid iteration cycles',
      stat: '8-12 weeks',
      gradient: 'from-blue-500/20 to-cyan-500/20'
    },
    {
      id: 2,
      icon: 'Shield',
      title: 'Value First',
      description: 'Start small, validate fast, and scale only when the value is proven',
      stat: 'Start Small',
      gradient: 'from-green-500/20 to-emerald-500/20'
    },
    {
      id: 3,
      icon: 'Users',
      title: 'Dedicated Team',
      description: 'Your dedicated technical co-founders working exclusively on your project',
      stat: '24/7',
      gradient: 'from-purple-500/20 to-pink-500/20'
    },
    {
      id: 4,
      icon: 'TrendingUp',
      title: 'Proven Results',
      description: '98% client satisfaction rate with 50+ successful enterprise projects delivered',
      stat: '98%',
      gradient: 'from-orange-500/20 to-red-500/20'
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-[#38322E] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-primary-foreground mb-4 md:mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-[#FAFAFA] font-body max-w-3xl mx-auto mb-8 md:mb-12">
            Join 50+ enterprise clients who trust Devontis Technologies to architect their digital transformation. Let's discuss how we can help you achieve your business goals.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Button
              variant="default"
              size="lg"
              iconName="Calendar"
              iconPosition="right"
              onClick={() => window.location.href = '/contact'}
              className="w-full sm:w-auto"
            >
              Schedule Free Consultation
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              onClick={() => window.location.href = 'tel:+91 9811143590'}
              className="w-full sm:w-auto bg-transparent text-primary-foreground border-primary-foreground hover:bg-primary-foreground/10"
            >
              Call Us Now
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {benefits?.map((benefit, index) => (
            <div
              key={benefit?.id}
              className="group relative bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 md:p-8 text-center hover:bg-white/10 hover:border-white/20 hover:shadow-2xl hover:shadow-white/10 transition-all duration-500 ease-out hover:-translate-y-2 overflow-hidden"
              style={{
                animationDelay: `${index * 100}ms`
              }}
            >
              {/* Gradient overlay on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${benefit?.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl`} />
              
              {/* Decorative corner accent */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#FFF4EC] rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Content */}
              <div className="relative z-10">
                {/* Icon with enhanced styling */}
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="absolute inset-0 bg-white/10 rounded-full blur-xl group-hover:bg-white/15 group-hover:blur-2xl transition-all duration-500" />
                  <div className="relative w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-white/20 to-white/10 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                    <Icon name={benefit?.icon} size={32} color="white" className="group-hover:scale-110 transition-transform duration-500" />
                  </div>
                </div>

                {/* Stat badge */}
                {/* <div className="inline-flex items-center justify-center mb-4">
                  <div className="relative px-4 py-1.5">
                    <span className="relative z-10 text-xs md:text-sm font-bold font-headline text-[#f97015] uppercase tracking-wider">
                      {benefit?.stat}
                    </span>
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#f97015]/50 to-transparent group-hover:via-[#f97015] transition-all duration-300" />
                    <div className="absolute inset-0 bg-[#f97015]/5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div> */}

                {/* Title */}
                <h3 className="text-xl md:text-2xl font-bold font-headline text-[#FAFAFA] mb-3 md:mb-4 group-hover:text-white transition-colors duration-300">
                  {benefit?.title}
                </h3>

                {/* Description */}
                <p className="text-sm md:text-base text-[#FAFAFA]/80 font-body leading-relaxed group-hover:text-[#FAFAFA]/90 transition-colors duration-300">
                  {benefit?.description}
                </p>
              </div>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6 text-primary-foreground/90">
            <div className="flex items-center space-x-2">
              <Icon name="Mail" size={20} />
              <span className="text-sm md:text-base font-body">contact@devontis.com</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Phone" size={20} />
              <span className="text-sm md:text-base font-body">+91 9811143590</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="MapPin" size={20} />
              <span className="text-sm md:text-base font-body">San Francisco, CA</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;